import React, { useState, useEffect, useRef, forwardRef, useImperativeHandle } from 'react';
import { useSelector } from 'react-redux';
import { getProject, getTaskLists, createTaskList, updateTaskList, deleteTaskList, getTaskByListId, createTask, updateTask, addAssigneeToTask, deleteTask, updateProject, deleteProject, updateProjectStatus, getProjectCategories, searchUsers, toggleProjectFavorite, removeUserFromProject, addUserToProject, updateTaskOrder } from '../../../utils/api';
import Modal from '../../common/Modal';
import TaskDetailSidePanel from './TaskDetailSidePanel';
import EditableTaskRow from './EditableTaskRow';
import PriorityDropdown from '../../common/PriorityDropdown';
import './SingleProjectView.scss';

// Default Task Input Row Component - Always visible
const DefaultTaskInputRow = forwardRef(({ taskListId, project, onSave, searchUsers }, ref) => {
    const [taskTitle, setTaskTitle] = useState('');
    const [assignee, setAssignee] = useState(null);
    const [dueDate, setDueDate] = useState('');
    const [priority, setPriority] = useState(0);
    const [assigneeSearchQuery, setAssigneeSearchQuery] = useState('');
    const [assigneeSearchResults, setAssigneeSearchResults] = useState([]);
    const [showAssigneeDropdown, setShowAssigneeDropdown] = useState(false);
    const [showDueDatePicker, setShowDueDatePicker] = useState(false);
    const assigneeSearchRef = useRef(null);
    const assigneeDropdownRef = useRef(null);
    const inputRef = useRef(null);
    const datePickerRef = useRef(null);
    const datePickerWrapperRef = useRef(null);
    const previousDateValueRef = useRef('');

    // Expose focus method to parent component
    useImperativeHandle(ref, () => ({
        focus: () => {
            if (inputRef.current) {
                inputRef.current.focus();
            }
        }
    }));

    // Filter assignee search from project users only
    useEffect(() => {
        if (!assigneeSearchQuery.trim()) {
            setAssigneeSearchResults([]);
            setShowAssigneeDropdown(false);
            return;
        }

        const debounceTimer = setTimeout(() => {
            const projectUsers = project?.users || [];
            const searchLower = assigneeSearchQuery.toLowerCase().trim();
            
            const filteredUsers = projectUsers.filter(user => {
                const name = (user.display_name || user.name || '').toLowerCase();
                const email = (user.user_email || user.email || '').toLowerCase();
                return name.includes(searchLower) || email.includes(searchLower);
            });
            
            setAssigneeSearchResults(filteredUsers);
            setShowAssigneeDropdown(true);
        }, 300);

        return () => clearTimeout(debounceTimer);
    }, [assigneeSearchQuery, project?.users]);

    // Close assignee dropdown and date picker when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                assigneeDropdownRef.current && 
                !assigneeDropdownRef.current.contains(event.target) &&
                assigneeSearchRef.current &&
                !assigneeSearchRef.current.contains(event.target)
            ) {
                setShowAssigneeDropdown(false);
            }
            // Close date picker if clicking outside
            if (showDueDatePicker && 
                datePickerWrapperRef.current && 
                !datePickerWrapperRef.current.contains(event.target) &&
                !event.target.closest('.asana-due-date-wrapper')) {
                setShowDueDatePicker(false);
            }
        };

        if (showAssigneeDropdown || showDueDatePicker) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showAssigneeDropdown, showDueDatePicker]);

    const handleSelectAssignee = (user) => {
        setAssignee(user);
        setAssigneeSearchQuery('');
        setShowAssigneeDropdown(false);
    };

    const handleSave = () => {
        if (!taskTitle.trim()) return;
        
        const taskData = {
            title: taskTitle.trim(),
            assignee: assignee,
            dueDate: dueDate,
            priority: priority
        };
        
        onSave(taskListId, taskData);
        
        // Reset form
        setTaskTitle('');
        setAssignee(null);
        setDueDate('');
        setPriority(0);
        if (inputRef.current) {
            inputRef.current.focus();
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && taskTitle.trim()) {
            e.preventDefault();
            handleSave();
        }
    };

    // Helper function to format date for date input (yyyy-MM-dd)
    const formatDateForInput = (dateValue) => {
        if (!dateValue) return '';
        try {
            // If it's already in yyyy-MM-dd format, return as is
            if (typeof dateValue === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateValue)) {
                return dateValue;
            }
            // Parse the date and format it
            const date = new Date(dateValue);
            if (isNaN(date.getTime())) return '';
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            return `${year}-${month}-${day}`;
        } catch (e) {
            return '';
        }
    };

    const handleDateInputChange = (e) => {
        const newDate = e.target.value;
        const previousDate = previousDateValueRef.current;

        // Only update if the date actually changed to a different valid date
        // This prevents auto-selection when navigating months
        if (newDate && newDate !== previousDate && newDate !== dueDate) {
            previousDateValueRef.current = newDate;
            setDueDate(newDate);
        }
    };

    return (
        <tr className="asana-task-row asana-default-task-input-row">
            <td className="asana-col-name">
                <div className="asana-task-name-cell">
                    <input
                        type="checkbox"
                        disabled
                        className="asana-task-checkbox"
                    />
                    <input
                        ref={inputRef}
                        type="text"
                        className="asana-default-task-input"
                        placeholder="Write a task name"
                        value={taskTitle}
                        onChange={(e) => setTaskTitle(e.target.value)}
                        onKeyDown={handleKeyDown}
                    />
                    {taskTitle.trim() && (
                        <button
                            type="button"
                            className="asana-save-task-arrow-btn"
                            onClick={handleSave}
                            title="Save task"
                        >
                            <i className="fa-solid fa-arrow-right"></i>
                        </button>
                    )}
                </div>
            </td>
            <td className="asana-col-assignee">
                <div className="asana-assignee-select-wrapper" ref={assigneeSearchRef}>
                    {assignee ? (
                        <div 
                            className="asana-assignee-selected"
                            onClick={() => {
                                setShowAssigneeDropdown(true);
                                setAssigneeSearchQuery('');
                            }}
                        >
                            <div className="asana-assignee-avatar">
                                {assignee.image ? (
                                    <img src={assignee.image} alt={assignee.display_name || assignee.name} />
                                ) : (
                                    <div className="asana-assignee-placeholder">
                                        {(assignee.display_name || assignee.name || 'U').charAt(0).toUpperCase()}
                                    </div>
                                )}
                            </div>
                            <button
                                type="button"
                                className="asana-remove-assignee"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setAssignee(null);
                                }}
                            >
                                <i className="fa-solid fa-times"></i>
                            </button>
                        </div>
                    ) : (
                        <div 
                            className="asana-assignee-placeholder-clickable"
                            onClick={() => setShowAssigneeDropdown(true)}
                        >
                            <i className="fa-solid fa-user-plus"></i>
                        </div>
                    )}
                    {showAssigneeDropdown && (
                        <div className="asana-assignee-dropdown" ref={assigneeDropdownRef}>
                            <input
                                type="text"
                                className="asana-assignee-search-input"
                                placeholder="Search assignee..."
                                value={assigneeSearchQuery}
                                onChange={(e) => setAssigneeSearchQuery(e.target.value)}
                                autoFocus
                            />
                            {assigneeSearchResults.length > 0 ? (
                                <div className="asana-assignee-results">
                                    {assigneeSearchResults.map((user) => (
                                        <div
                                            key={user.ID || user.id}
                                            className="asana-assignee-result-item"
                                            onClick={() => handleSelectAssignee(user)}
                                        >
                                            <div className="asana-assignee-avatar">
                                                {user.image ? (
                                                    <img src={user.image} alt={user.display_name || user.name} />
                                                ) : (
                                                    <div className="asana-assignee-placeholder">
                                                        {(user.display_name || user.name || 'U').charAt(0).toUpperCase()}
                                                    </div>
                                                )}
                                            </div>
                                            <span>{user.display_name || user.name || 'User'}</span>
                                        </div>
                                    ))}
                                </div>
                            ) : assigneeSearchQuery.trim() ? (
                                <div className="asana-search-empty">No users found</div>
                            ) : (
                                <div className="asana-assignee-results">
                                    {(project?.users || []).map((user) => (
                                        <div
                                            key={user.ID || user.id}
                                            className="asana-assignee-result-item"
                                            onClick={() => handleSelectAssignee(user)}
                                        >
                                            <div className="asana-assignee-avatar">
                                                {user.image || user.avatar_url ? (
                                                    <img src={user.image || user.avatar_url} alt={user.display_name || user.name} />
                                                ) : (
                                                    <div className="asana-assignee-placeholder">
                                                        {(user.display_name || user.name || 'U').charAt(0).toUpperCase()}
                                                    </div>
                                                )}
                                            </div>
                                            <span>{user.display_name || user.name || 'User'}</span>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </td>
            <td className="asana-col-due">
                <div className="asana-due-date-wrapper">
                    {dueDate ? (
                        <div 
                            className="asana-due-date-selected"
                            onClick={() => setShowDueDatePicker(true)}
                        >
                            <span>{new Date(dueDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>
                            <button
                                type="button"
                                className="asana-remove-due-date"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setDueDate('');
                                }}
                            >
                                <i className="fa-solid fa-times"></i>
                            </button>
                        </div>
                    ) : (
                        <div 
                            className="asana-due-date-placeholder-clickable"
                            onClick={() => setShowDueDatePicker(true)}
                        >
                            <i className="fa-solid fa-calendar"></i>
                        </div>
                    )}
                    {showDueDatePicker && (
                        <div className="asana-due-date-picker-wrapper" ref={datePickerWrapperRef}>
                            <input
                                ref={datePickerRef}
                                type="date"
                                className="asana-due-date-picker"
                                value={formatDateForInput(dueDate)}
                                onChange={handleDateInputChange}
                                autoFocus
                            />
                        </div>
                    )}
                </div>
            </td>
            <td className="asana-col-priority">
                <PriorityDropdown
                    currentPriority={priority}
                    onPriorityChange={setPriority}
                    className="asana-priority-wrapper"
                    showAsPill={true}
                    handleShowDropdown={false}
                />
            </td>
            <td className="asana-col-actions">
                {/* Empty cell for actions column */}
            </td>
        </tr>
    );
});


const SingleProjectView = ({ projectId, onBack, currentUserId }) => {
    const jwtToken = useSelector((state) => state.auth.jwtToken);
    
    const [project, setProject] = useState(null);
    const [taskLists, setTaskLists] = useState([]);
    const [tasks, setTasks] = useState({}); // { taskListId: [tasks] }
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [openMenuId, setOpenMenuId] = useState(null);
    const [quickAddTaskTitle, setQuickAddTaskTitle] = useState('');
    const [quickAddTaskListId, setQuickAddTaskListId] = useState(null);
    
    // Modal states
    const [isTaskListModalOpen, setIsTaskListModalOpen] = useState(false);
    const [isTaskModalOpen, setIsTaskModalOpen] = useState(false);
    const [isEditProjectModalOpen, setIsEditProjectModalOpen] = useState(false);
    const [isManageUsersModalOpen, setIsManageUsersModalOpen] = useState(false);
    const [isEditSectionModalOpen, setIsEditSectionModalOpen] = useState(false);
    
    // Task detail side panel states
    const [selectedTaskIdForDetail, setSelectedTaskIdForDetail] = useState(null);
    const [selectedTaskListId, setSelectedTaskListId] = useState(null);
    const [editingSectionTaskListId, setEditingSectionTaskListId] = useState(null);
    const [taskListFormData, setTaskListFormData] = useState({ title: '', description: '' });
    const [editSectionFormData, setEditSectionFormData] = useState({ title: '', description: '' });
    const [taskFormData, setTaskFormData] = useState({ title: '', description: '', task_list_id: '' });
    const [editProjectFormData, setEditProjectFormData] = useState({ title: '', description: '', category: '', assignees: [] });
    const [categories, setCategories] = useState([]);
    
    // User search states for edit modal
    const [userSearchQuery, setUserSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [isSearching, setIsSearching] = useState(false);
    const [showUserDropdown, setShowUserDropdown] = useState(false);
    const userSearchRef = useRef(null);
    const userDropdownRef = useRef(null);
    
    // User search states for manage users modal
    const [manageUsersSearchQuery, setManageUsersSearchQuery] = useState('');
    const [manageUsersSearchResults, setManageUsersSearchResults] = useState([]);
    const [isManageUsersSearching, setIsManageUsersSearching] = useState(false);
    const manageUsersSearchRef = useRef(null);
    
    // UI states
    const [showDescription, setShowDescription] = useState(false);
    const [openActionMenu, setOpenActionMenu] = useState(false);
    const actionMenuRef = useRef(null);
    
    // Asana-style states
    const [collapsedSections, setCollapsedSections] = useState({}); // { taskListId: true/false }
    const [inlineTaskInputs, setInlineTaskInputs] = useState({}); // { taskListId: '' }
    const [isFavorite, setIsFavorite] = useState(false);
    const [showAddTaskDropdown, setShowAddTaskDropdown] = useState(false);
    const [showStatusDropdown, setShowStatusDropdown] = useState(false);
    const [openUsersPopupId, setOpenUsersPopupId] = useState(null);
    const [showAddUserPopup, setShowAddUserPopup] = useState(false);
    const [addUserSearchQuery, setAddUserSearchQuery] = useState('');
    const [addUserSearchResults, setAddUserSearchResults] = useState([]);
    const [isAddUserSearching, setIsAddUserSearching] = useState(false);
    const [openSectionMenuId, setOpenSectionMenuId] = useState(null); // Track which section dropdown is open
    const addTaskDropdownRef = useRef(null);
    const statusDropdownRef = useRef(null);
    const usersPopupRef = useRef(null);
    const addUserPopupRef = useRef(null);
    const addUserSearchRef = useRef(null);
    const sectionMenuRefs = useRef({}); // Store refs for each section dropdown
    
    // Inline editing states
    const [editingSectionId, setEditingSectionId] = useState(null); // taskListId being edited
    const [editingSectionTitle, setEditingSectionTitle] = useState(''); // temporary title while editing
    const [pendingNewSectionId, setPendingNewSectionId] = useState(null); // ID of section waiting to be edited
    
    // Draft tasks (tasks being created inline)
    const [draftTasks, setDraftTasks] = useState({}); // { taskListId: { id: 'draft-xxx', title: '', assignee: null, dueDate: '' } }
    const [editingTaskId, setEditingTaskId] = useState(null); // Currently editing task ID
    const [editingTaskTitle, setEditingTaskTitle] = useState({}); // { taskId: 'title' }
    const [openTaskActionMenu, setOpenTaskActionMenu] = useState(null); // taskId of open menu
    const taskActionMenuRefs = useRef({}); // { taskId: ref }
    const defaultTaskInputRefs = useRef({}); // { taskListId: ref }
    
    // Drag and drop state
    const [draggedTaskId, setDraggedTaskId] = useState(null);
    const [draggedOverTaskId, setDraggedOverTaskId] = useState(null);
    const [draggedTaskListId, setDraggedTaskListId] = useState(null);

    // Fetch project details
    const fetchProject = async () => {
        if (!jwtToken || !projectId) return;
        
        setIsLoading(true);
        setError(null);
        
        try {
            const response = await getProject(projectId);
            if (response && response.data) {
                const projectData = response.data;
                setProject(projectData);
                // Initialize favorite state from project data
                const isFavourite = projectData.favourite || (projectData.favourite !== '0' && projectData.favourite !== '' && projectData.favourite !== false);
                setIsFavorite(isFavourite);
            }
        } catch (err) {
            console.error('Failed to fetch project:', err);
            setError(err.message || 'Failed to load project');
        } finally {
            setIsLoading(false);
        }
    };

    // Fetch task lists
    const fetchTaskLists = async () => {
        if (!jwtToken || !projectId) return;
        
        try {
            const response = await getTaskLists(projectId);
            if (response && response.data) {
                const lists = Array.isArray(response.data) ? response.data : [response.data];
                setTaskLists(lists);
                
                // If tasks are nested in task lists, extract them
                const tasksMap = {};
                lists.forEach(list => {
                    if (list.tasks && Array.isArray(list.tasks)) {
                        tasksMap[list.id] = list.tasks;
                    } else {
                        // If tasks not nested, fetch separately (fallback)
                        fetchTasksForList(list.id);
                    }
                });
                
                // Update tasks state if we have nested tasks
                if (Object.keys(tasksMap).length > 0) {
                    setTasks(tasksMap);
                }
            }
        } catch (err) {
            console.error('Failed to fetch task lists:', err);
        }
    };

    // Fetch tasks for a specific task list
    const fetchTasksForList = async (taskListId) => {
        if (!jwtToken || !projectId) return;
        
        try {
            const response = await getTaskByListId(projectId, taskListId);
            if (response && response.data) {
                const taskArray = Array.isArray(response.data) ? response.data : [response.data];
                setTasks(prev => ({
                    ...prev,
                    [taskListId]: taskArray
                }));
            }
        } catch (err) {
            console.error('Failed to fetch tasks:', err);
        }
    };

    // Fetch categories
    const fetchCategories = async () => {
        if (!jwtToken) return;
        
        try {
            const response = await getProjectCategories();
            if (response && response.data) {
                const fetchedCategories = response.data.categories || response.data.data || response.data || [];
                setCategories(Array.isArray(fetchedCategories) ? fetchedCategories : []);
            }
        } catch (err) {
            console.error('Failed to fetch categories:', err);
        }
    };

    // Helper function to update URL with task parameter
    const updateURLWithTask = (taskId) => {
        if (!taskId) {
            // Remove task parameter from URL
            const url = new URL(window.location.href);
            url.searchParams.delete('task');
            window.history.replaceState({}, '', url.toString());
            return;
        }
        
        // Add or update task parameter in URL
        const url = new URL(window.location.href);
        url.searchParams.set('task', taskId);
        window.history.replaceState({}, '', url.toString());
    };

    // Helper function to get task ID from URL
    const getTaskIdFromURL = () => {
        const urlParams = new URLSearchParams(window.location.search);
        return urlParams.get('task');
    };

    // Fetch data on mount
    useEffect(() => {
        if (jwtToken && projectId) {
            fetchProject();
            fetchTaskLists();
            fetchCategories();
        }
    }, [jwtToken, projectId]);

    // Check URL for task parameter on mount and after task lists are loaded
    useEffect(() => {
        if (jwtToken && projectId && taskLists.length > 0 && Object.keys(tasks).length > 0) {
            const taskIdFromURL = getTaskIdFromURL();
            setSelectedTaskIdForDetail(taskIdFromURL);
            if (taskIdFromURL && !selectedTaskIdForDetail) {
                setSelectedTaskIdForDetail(taskIdFromURL);
                
                // Verify task exists in the loaded tasks (including subtasks)
                // let taskExists = false;
                // for (const taskListId in tasks) {
                //     const taskList = tasks[taskListId];
                //     for (const task of taskList) {
                //         // Check if it's the task itself
                //         if (String(task.id) === String(taskIdFromURL)) {
                //             taskExists = true;
                //             break;
                //         }
                //         // Check if it's a subtask
                //         if (task.subtasks && Array.isArray(task.subtasks)) {
                //             if (task.subtasks.some(subtask => {
                //                 const subtaskId = subtask.id || (subtask.subtaskData && subtask.subtaskData.id);
                //                 return String(subtaskId) === String(taskIdFromURL);
                //             })) {
                //                 taskExists = true;
                //                 break;
                //             }
                //         }
                //     }
                //     if (taskExists) break;
                // }
                
                // if (taskExists) {
                //     setSelectedTaskIdForDetail(taskIdFromURL);
                // } else {
                //     // Task not found, remove from URL
                //     updateURLWithTask(null);
                // }
            }
        }
    }, [jwtToken, projectId, taskLists, tasks, selectedTaskIdForDetail]);

    // Handle pending new section - set it to editing mode when it appears
    useEffect(() => {
        if (pendingNewSectionId && taskLists.length > 0) {
            const newSection = taskLists.find(list => list.id === pendingNewSectionId);
            if (newSection) {
                // Section found, set it to editing mode
                setCollapsedSections(prev => ({
                    ...prev,
                    [pendingNewSectionId]: false
                }));
                setTimeout(() => {
                    setEditingSectionId(pendingNewSectionId);
                    setEditingSectionTitle(newSection.title || '');
                    setPendingNewSectionId(null);
                }, 100);
            }
        }
    }, [taskLists, pendingNewSectionId]);

    // Close action menu when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (actionMenuRef.current && !actionMenuRef.current.contains(event.target)) {
                setOpenActionMenu(false);
            }
            if (addTaskDropdownRef.current && !addTaskDropdownRef.current.contains(event.target)) {
                setShowAddTaskDropdown(false);
            }
            if (statusDropdownRef.current && !statusDropdownRef.current.contains(event.target)) {
                setShowStatusDropdown(false);
            }
            if (usersPopupRef.current && !usersPopupRef.current.contains(event.target)) {
                setOpenUsersPopupId(null);
            }
            if (addUserPopupRef.current && !addUserPopupRef.current.contains(event.target) && 
                addUserSearchRef.current && !addUserSearchRef.current.contains(event.target)) {
                setShowAddUserPopup(false);
            }
            // Close section menu if clicking outside
            if (openSectionMenuId) {
                const menuRef = sectionMenuRefs.current[openSectionMenuId];
                if (menuRef && !menuRef.contains(event.target)) {
                    setOpenSectionMenuId(null);
                }
            }
        };

        if (openActionMenu || showAddTaskDropdown || showStatusDropdown || openUsersPopupId || showAddUserPopup || openSectionMenuId) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [openActionMenu, showAddTaskDropdown, showStatusDropdown, openUsersPopupId, showAddUserPopup, openSectionMenuId]);

    // Debounce user search for edit modal
    useEffect(() => {
        if (!userSearchQuery.trim() || !isManageUsersModalOpen) {
            setSearchResults([]);
            setShowUserDropdown(false);
            return;
        }

        const debounceTimer = setTimeout(async () => {
            setIsSearching(true);
            try {
                const response = await searchUsers({ search: userSearchQuery, page: 1, size: 10 });
                if (response && response.data && response.data.users) {
                    const users = response.data.users.users || [];
                    // Filter out already assigned users from the project
                    const currentUserIds = (project?.users || []).map(u => u.ID || u.id);
                    const filteredUsers = users.filter(user => 
                        !currentUserIds.includes(user.ID || user.id)
                    );
                    setSearchResults(filteredUsers);
                    setShowUserDropdown(true);
                } else {
                    setSearchResults([]);
                    setShowUserDropdown(false);
                }
            } catch (err) {
                console.error('Error searching users:', err);
                setSearchResults([]);
                setShowUserDropdown(false);
            } finally {
                setIsSearching(false);
            }
        }, 300);

        return () => clearTimeout(debounceTimer);
    }, [userSearchQuery, project?.users, isManageUsersModalOpen, project]);

    // Close user dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                userDropdownRef.current && 
                !userDropdownRef.current.contains(event.target) &&
                userSearchRef.current &&
                !userSearchRef.current.contains(event.target)
            ) {
                setShowUserDropdown(false);
            }
        };

        if (showUserDropdown && isEditProjectModalOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showUserDropdown, isEditProjectModalOpen]);

    // Handle create task list
    const handleCreateTaskList = async (e = null) => {
        if (e) {
            e.preventDefault();
        }
        if (!jwtToken || !projectId) return;
        
        if (!taskListFormData.title.trim()) {
            alert('Please enter a section title');
            return;
        }

        try {
            const createData = {
                ...taskListFormData,
                order: taskLists.length + 1
            };
            
            const response = await createTaskList(projectId, createData);
            if (response && response.data) {
                await fetchTaskLists();
                setIsTaskListModalOpen(false);
                setTaskListFormData({ title: '', description: '' });
            } else {
                alert('Failed to create section. Please try again.');
            }
        } catch (err) {
            console.error('Failed to create section:', err);
            alert('Failed to create section. Please try again.');
        }
    };

    const handleCreateTaskListFromDropdown = async () => {
        if (!jwtToken || !projectId) return;

        try {
            const createData = {
                title: 'Untitled section',
                description: '',
                order: taskLists.length + 1
            };
            
            const response = await createTaskList(projectId, createData);
            if (response && response.data) {
                await fetchTaskLists();
                setIsTaskListModalOpen(false);
                setTaskListFormData({ title: '', description: '' });
            } else {
                alert('Failed to create section. Please try again.');
            }
        } catch (err) {
            console.error('Failed to create section:', err);
            alert('Failed to create section. Please try again.');
        }
    };

    // Handle create task
    const handleCreateTask = async (e) => {
        e.preventDefault();
        if (!jwtToken || !projectId) return;
        
        if (!taskFormData.title.trim()) {
            alert('Please enter a task title');
            return;
        }

        if (!taskFormData.task_list_id) {
            alert('Please select a task list');
            return;
        }

        try {
            const response = await createTask(projectId, taskFormData);
            if (response && response.data) {
                await fetchTasksForList(taskFormData.task_list_id);
                setIsTaskModalOpen(false);
                setTaskFormData({ title: '', description: '', task_list_id: '' });
                setSelectedTaskListId(null);
            } else {
                alert('Failed to create task. Please try again.');
            }
        } catch (err) {
            console.error('Failed to create task:', err);
            alert('Failed to create task. Please try again.');
        }
    };

    // Handle open task modal
    const handleOpenTaskModal = (taskListId) => {
        setSelectedTaskListId(taskListId);
        setTaskFormData({
            title: '',
            description: '',
            task_list_id: taskListId
        });
        setIsTaskModalOpen(true);
    };

    // Handle open edit project modal
    const handleOpenEditModal = () => {
        if (project) {
            // Get assigned users from project data
            const assignedUsers = project.users || project.assignees || [];
            setEditProjectFormData({
                title: project.title || project.name || '',
                description: project.description || '',
                category: project.categories && project.categories.length > 0 ? (project.categories[0].id || project.categories[0].category_id) : '',
                assignees: Array.isArray(assignedUsers) ? assignedUsers : []
            });
            setUserSearchQuery('');
            setShowUserDropdown(false);
            setIsEditProjectModalOpen(true);
        }
    };

    // Handle add user in edit modal
    const handleAddUser = (user) => {
        if (!editProjectFormData.assignees.some(assignee => 
            (assignee.ID || assignee.id) === (user.ID || user.id)
        )) {
            setEditProjectFormData(prev => ({
                ...prev,
                assignees: [...prev.assignees, user]
            }));
        }
        setUserSearchQuery('');
        setShowUserDropdown(false);
    };

    // Handle remove user in edit modal
    const handleRemoveUser = (userId) => {
        setEditProjectFormData(prev => ({
            ...prev,
            assignees: prev.assignees.filter(assignee => 
                (assignee.ID || assignee.id) !== userId
            )
        }));
    };

    // Handle update project
    const handleUpdateProject = async (e) => {
        e.preventDefault();
        if (!jwtToken || !projectId) return;
        
        if (!editProjectFormData.title.trim()) {
            alert('Please enter a project title');
            return;
        }

        try {
            const updateData = {
                title: editProjectFormData.title.trim(),
                description: editProjectFormData.description.trim() || '',
                category: editProjectFormData.category || '',
                // assignees: editProjectFormData.assignees.map(user => user.ID || user.id)
            };

            console.log('updateData', updateData);

            const response = await updateProject(projectId, updateData);
            if (response && response.data) {
                await fetchProject();
                setIsEditProjectModalOpen(false);
                setEditProjectFormData({ title: '', description: '', category: '', assignees: [] });
                setUserSearchQuery('');
            } else {
                alert('Failed to update project. Please try again.');
            }
        } catch (err) {
            console.error('Failed to update project:', err);
            alert('Failed to update project. Please try again.');
        }
    };

    // Handle delete project
    const handleDeleteProject = async () => {
        if (!jwtToken || !projectId) return;
        
        if (!window.confirm('Are you sure you want to delete this project? This action cannot be undone.')) {
            return;
        }

        try {
            const response = await deleteProject(projectId);
            if (response && response.data) {
                if (onBack) {
                    onBack();
                }
            } else {
                alert('Failed to delete project. Please try again.');
            }
        } catch (err) {
            console.error('Failed to delete project:', err);
            alert('Failed to delete project. Please try again.');
        }
    };

    // Handle change project status
    const handleChangeStatus = async (newStatus) => {
        if (!jwtToken || !projectId) return;
        
        try {
            const response = await updateProjectStatus(projectId, newStatus);
            if (response && response.data) {
                await fetchProject();
                setOpenActionMenu(false);
            } else {
                alert('Failed to update project status. Please try again.');
            }
        } catch (err) {
            console.error('Failed to update project status:', err);
            alert('Failed to update project status. Please try again.');
        }
    };

    // Handle toggle favorite
    const handleToggleFavorite = async () => {
        if (!jwtToken || !projectId) return;
        
        const newFavouriteStatus = !isFavorite;
        try {
            const response = await toggleProjectFavorite(projectId, newFavouriteStatus);

            if (response && response.data) {
                setIsFavorite(newFavouriteStatus);
                // Update project state
                setProject(prev => ({
                    ...prev,
                    favourite: newFavouriteStatus
                }));
            } else {
                alert('Failed to update favorite status. Please try again.');
            }
        } catch (err) {
            console.error('Failed to toggle favorite:', err);
            alert('Failed to update favorite status. Please try again.');
        }
    };

    // Close menu when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (openMenuId && !event.target.closest('.octopus-table-menu-wrapper')) {
                setOpenMenuId(null);
            }
        };

        if (openMenuId) {
            document.addEventListener('click', handleClickOutside);
        }

        return () => {
            document.removeEventListener('click', handleClickOutside);
        };
    }, [openMenuId]);

    // Handle inline task creation within a section - create draft row instead
    const handleInlineTaskSubmit = (taskListId, e) => {
        e.preventDefault();
        const taskTitle = inlineTaskInputs[taskListId] || '';
        if (!taskTitle.trim()) return;

        // Check if there's already a draft task for this section
        if (draftTasks[taskListId]) {
            return; // Don't create another draft if one already exists
        }

        // Create draft task with the entered title
        const draftId = `draft-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
        setDraftTasks(prev => ({
            ...prev,
            [taskListId]: {
                id: draftId,
                title: taskTitle.trim(),
                assignee: null,
                dueDate: '',
                task_list_id: taskListId
            }
        }));
        setEditingTaskId(draftId);
        // Clear the input
        setInlineTaskInputs(prev => ({
            ...prev,
            [taskListId]: ''
        }));
    };

    // Handle quick add task (focus the default input row)
    const handleQuickAddTask = (taskListId) => {
        const inputRef = defaultTaskInputRefs.current[taskListId];
        if (inputRef && typeof inputRef.focus === 'function') {
            inputRef.focus();
        }
    };

    // Handle open add section modal
    const handleQuickAddSection = () => {
        // Reset form data and open modal
        setTaskListFormData({ title: '', description: '' });
        setIsTaskListModalOpen(true);
    };

    // Handle open edit section modal
    const handleOpenEditSectionModal = (taskListId) => {
        const taskList = taskLists.find(list => list.id === taskListId);
        if (taskList) {
            setEditSectionFormData({
                title: taskList.title || taskList.name || '',
                description: taskList.description || ''
            });
            setEditingSectionTaskListId(taskListId);
            setIsEditSectionModalOpen(true);
        }
    };

    // Handle update section from modal
    const handleUpdateSection = async (e) => {
        e.preventDefault();
        if (!jwtToken || !projectId || !editingSectionTaskListId) return;
        
        if (!editSectionFormData.title.trim()) {
            alert('Please enter a section title');
            return;
        }

        try {
            const response = await updateTaskList(projectId, editingSectionTaskListId, {
                title: editSectionFormData.title.trim(),
                description: editSectionFormData.description.trim() || undefined
            });
            
            if (response && response.data) {
                await fetchTaskLists();
                setIsEditSectionModalOpen(false);
                setEditSectionFormData({ title: '', description: '' });
                setEditingSectionTaskListId(null);
            } else {
                alert('Failed to update section. Please try again.');
            }
        } catch (err) {
            console.error('Failed to update section:', err);
            alert('Failed to update section. Please try again.');
        }
    };

    // Handle delete section
    const handleDeleteSection = async (taskListId) => {
        if (!jwtToken || !projectId || !taskListId) return;
        
        if (taskLists.length <= 1) {
            alert('Cannot delete the last section. A project must have at least one section.');
            return;
        }

        if (!window.confirm('Are you sure you want to delete this section? All tasks in this section will also be deleted. This action cannot be undone.')) {
            return;
        }

        try {
            const response = await deleteTaskList(projectId, taskListId);
            if (response && response.data) {
                await fetchTaskLists();
            } else {
                alert('Failed to delete section. Please try again.');
            }
        } catch (err) {
            console.error('Failed to delete section:', err);
            alert('Failed to delete section. Please try again.');
        }
    };

    // Handle start editing section title
    const handleStartEditSection = (taskListId, currentTitle) => {
        setEditingSectionId(taskListId);
        setEditingSectionTitle(currentTitle || 'Untitled section');
    };

    // Handle save section title
    const handleSaveSectionTitle = async (taskListId) => {
        if (!jwtToken || !projectId || !taskListId) return;

        const newTitle = editingSectionTitle.trim() || 'Untitled section';
        
        // Don't save if still empty and user hasn't typed anything
        if (!editingSectionTitle.trim() && editingSectionTitle === '') {
            // If it's a new section with empty title, save as "Untitled section"
        }
        
        try {
            // Update on backend first
            const response = await updateTaskList(projectId, taskListId, {
                title: newTitle
            });
            
            if (response && response.data) {
                // Update local state after successful save
                setTaskLists(prev => prev.map(list => 
                    list.id === taskListId 
                        ? { ...list, title: newTitle, name: newTitle }
                        : list
                ));
            } else {
                // If update fails, refresh from server
                await fetchTaskLists();
            }
            
            setEditingSectionId(null);
            setEditingSectionTitle('');
        } catch (err) {
            console.error('Failed to update section title:', err);
            // Refresh from server on error
            await fetchTaskLists();
            setEditingSectionId(null);
            setEditingSectionTitle('');
        }
    };

    // Handle cancel editing section title
    const handleCancelEditSection = async () => {
        const currentEditingId = editingSectionId;
        const currentTitle = editingSectionTitle.trim();
        
        setEditingSectionId(null);
        setEditingSectionTitle('');
        
        // If title is empty when canceling, save as "Untitled section"
        if (currentEditingId && !currentTitle) {
            try {
                await updateTaskList(projectId, currentEditingId, {
                    title: 'Untitled section'
                });
                await fetchTaskLists();
            } catch (err) {
                console.error('Failed to update section title:', err);
                await fetchTaskLists();
            }
        }
    };

    // Toggle section collapse
    const toggleSection = (taskListId) => {
        setCollapsedSections(prev => ({
            ...prev,
            [taskListId]: !prev[taskListId]
        }));
    };

    // Handle add task from dropdown - create draft task row
    const handleAddTaskFromDropdown = (taskListId) => {
        // Check if there's already a draft task for this section
        if (draftTasks[taskListId]) {
            return; // Don't create another draft if one already exists
        }
        
        const draftId = `draft-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
        setDraftTasks(prev => ({
            ...prev,
            [taskListId]: {
                id: draftId,
                title: '',
                assignee: null,
                dueDate: '',
                task_list_id: taskListId
            }
        }));
        setEditingTaskId(draftId);
        setShowAddTaskDropdown(false);
    };

    // Handle save draft task (works with both old draft task structure and new default input row)
    const handleSaveDraftTask = async (taskListId, taskData) => {
        if (!jwtToken || !projectId) return;
        
        const title = taskData.title;
        if (!title || !title.trim()) {
            alert('Please enter a task title');
            return;
        }

        try {
            const createData = {
                title: title.trim(),
                description: '',
                task_list_id: taskListId,
                due_date: taskData.dueDate || taskData.due_date || undefined,
                assignees: (taskData.assignee ? [taskData.assignee.ID || taskData.assignee.id] : undefined) || 
                          (taskData.assignees ? taskData.assignees.map(a => a.ID || a.id) : undefined)
            };

            const response = await createTask(projectId, createData);
            if (response && response.data) {
                // Refresh tasks
                await fetchTasksForList(taskListId);
            } else {
                alert('Failed to create task. Please try again.');
            }
        } catch (err) {
            console.error('Failed to create task:', err);
            alert('Failed to create task. Please try again.');
        }
    };

    // Handle cancel draft task
    const handleCancelDraftTask = (taskListId) => {
        setDraftTasks(prev => {
            const newDrafts = { ...prev };
            delete newDrafts[taskListId];
            return newDrafts;
        });
        setEditingTaskId(null);
    };

    // Handle update draft task field
    const handleUpdateDraftTask = (taskListId, field, value) => {
        setDraftTasks(prev => ({
            ...prev,
            [taskListId]: {
                ...prev[taskListId],
                [field]: value
            }
        }));
    };

    // Debounce add user search
    useEffect(() => {
        if (!addUserSearchQuery.trim() || !showAddUserPopup || !project) {
            setAddUserSearchResults([]);
            return;
        }

        const debounceTimer = setTimeout(async () => {
            setIsAddUserSearching(true);
            try {
                const response = await searchUsers({ search: addUserSearchQuery, page: 1, size: 10 });
                if (response && response.data && response.data.users) {
                    const users = response.data.users.users || [];
                    // Filter out already assigned users
                    const currentUserIds = (project?.users || []).map(u => u.ID || u.id);
                    const filteredUsers = users.filter(user => 
                        !currentUserIds.includes(user.ID || user.id)
                    );
                    setAddUserSearchResults(filteredUsers);
                } else {
                    setAddUserSearchResults([]);
                }
            } catch (err) {
                console.error('Error searching users:', err);
                setAddUserSearchResults([]);
            } finally {
                setIsAddUserSearching(false);
            }
        }, 300);

        return () => clearTimeout(debounceTimer);
    }, [addUserSearchQuery, project?.users, showAddUserPopup, project]);

    // Handle add user to project
    const handleAddUserToProject = async (user) => {
        if (!jwtToken || !projectId || !project) return;

        try {
            const currentUserIds = (project?.users || []).map(u => u.ID || u.id);
            const newUserIds = [...currentUserIds, user.ID || user.id];

            const response = await updateProject(projectId, {
                assignees: newUserIds
            });

            if (response && response.data) {
                await fetchProject();
                setAddUserSearchQuery('');
                setAddUserSearchResults([]);
                setShowAddUserPopup(false);
            } else {
                alert('Failed to add user. Please try again.');
            }
        } catch (err) {
            console.error('Failed to add user:', err);
            alert('Failed to add user. Please try again.');
        }
    };

    // Handle add user from Manage Users Modal
    const handleAddUserFromModal = async (user) => {
        if (!jwtToken || !projectId || !project) return;

        try {
            const currentUserIds = (project?.users || []).map(u => u.ID || u.id);
            
            // Check if user is already assigned
            if (currentUserIds.includes(user.ID || user.id)) {
                alert('User is already assigned to this project.');
                return;
            }

            const response = await addUserToProject(projectId, user.ID || user.id);

            if (response && response.data) {
                await fetchProject();
                // Clear search query and results
                setUserSearchQuery('');
                setSearchResults([]);
                setShowUserDropdown(false);
            } else {
                alert('Failed to add user. Please try again.');
            }
        } catch (err) {
            console.error('Failed to add user:', err);
            alert('Failed to add user. Please try again.');
        }
    };

    // Handle remove user from project
    const handleRemoveUserFromProject = async (userId) => {
        if (!jwtToken || !projectId || !project) return;

        if (!window.confirm('Are you sure you want to remove this user from the project?')) {
            return;
        }

        try {
            const response = await removeUserFromProject(projectId, userId);

            if (response && response.data) {
                await fetchProject();
            } else {
                alert('Failed to remove user. Please try again.');
            }
        } catch (err) {
            console.error('Failed to remove user:', err);
            alert('Failed to remove user. Please try again.');
        }
    };

    // Handle update task
    const handleUpdateTask = async (projectId, taskId, taskListId, taskData) => {
        if (!jwtToken || !projectId || !taskId) return;


        console.log('handleUpdateTask', projectId, taskId, taskListId, taskData);

        try {
            const response = await updateTask(projectId, taskId, taskData);
            if (response && response.data) {
                if(taskListId) {
                    await fetchTasksForList(taskListId);
                } else {
                    await fetchTaskLists();
                }
                // Find which task list this task belongs to and refresh
                // for (const taskList of taskLists) {
                //     const taskListTasks = tasks[taskList.id] || [];
                //     if (taskListTasks.some(t => t.id === taskId)) {
                //         await fetchTasksForList(taskList.id);
                //         break;
                //     }
                // }
            } else {
                alert('Failed to update task. Please try again.');
            }
        } catch (err) {
            console.error('Failed to update task:', err);
            alert('Failed to update task. Please try again.');
        }
    };
    const handleAddAssigneeToTask = async (projectId, taskId, taskListId, assigneeId) => {
        if (!jwtToken || !projectId) return;
        try {
            const response = await addAssigneeToTask(projectId, taskId, assigneeId);
            if (response && response.data) {
                if(taskListId) {
                    await fetchTasksForList(taskListId);
                } else {
                    await fetchTaskLists();
                }
            }
        } catch (err) {
            console.error('Failed to add assignee to task:', err);
            alert('Failed to add assignee to task. Please try again.');
        }
    };

    // Handle delete task
    const handleDeleteTask = async (projectId, taskId, taskListId, parentTaskId = null) => {
        if (!jwtToken || !projectId) return;

        console.log('handleDeleteTask', projectId, taskId, taskListId, parentTaskId);

        try {
            const response = await deleteTask(projectId, taskListId, taskId);
            if (response && response.data) {
                if(taskListId) {
                    await fetchTasksForList(taskListId);
                } else {
                    await fetchTaskLists();
                }
                // Close side panel if the deleted task is currently open
                if (selectedTaskIdForDetail && String(selectedTaskIdForDetail) === String(taskId)) {
                    setSelectedTaskIdForDetail(null);
                    updateURLWithTask(null);
                }
            } else {
                alert('Failed to delete task. Please try again.');
            }
        } catch (err) {
            console.error('Failed to delete task:', err);
            alert('Failed to delete task. Please try again.');
        }
    };

    // Handle create subtask
    const handleCreateSubtask = async (projectId, parentTaskId, taskListId, subtaskData) => {
        if (!jwtToken || !projectId || !parentTaskId || !taskListId) return;
        

        try {
            const createData = {
                title: subtaskData.title,
                description: subtaskData.description || '',
                task_list_id: taskListId,
                parent_id: parentTaskId,
            };

            const response = await createTask(projectId, createData);
            if (response && response.data) {
                await fetchTasksForList(taskListId);
            } else {
                alert('Failed to create subtask. Please try again.');
            }
        } catch (err) {
            console.error('Failed to create subtask:', err);
            alert('Failed to create subtask. Please try again.');
        }
    };

    // Handle update subtask
    const handleUpdateSubtask = async (projectId, subtaskId, taskListId, subtaskData) => {
        if (!jwtToken || !projectId) return;

        try {
            const response = await updateTask(projectId, subtaskId, subtaskData);
            if (response && response.data) {
                await fetchTasksForList(taskListId);
            } else {
                alert('Failed to update subtask. Please try again.');
            }
        } catch (err) {
            console.error('Failed to update subtask:', err);
            alert('Failed to update subtask. Please try again.');
        }
    };

    // Handle delete subtask
    const handleDeleteSubtask = async (projectId, subtaskId, taskListId, parentTaskId = null) => {
        if (!jwtToken || !projectId) return;

        try {
            const response = await deleteTask(projectId, taskListId, subtaskId);
            if (response && response.data) {
                await fetchTasksForList(taskListId);
            } else {
                alert('Failed to delete subtask. Please try again.');
            }
        } catch (err) {
            console.error('Failed to delete subtask:', err);
            alert('Failed to delete subtask. Please try again.');
        }
    };

    // Handle open task detail side panel
    const handleOpenTaskDetail = (projectId, taskId) => {
        setSelectedTaskIdForDetail(taskId);
        updateURLWithTask(taskId);
    };
    const handleOnNotFound = () => {
        setSelectedTaskIdForDetail(null);
        updateURLWithTask(null);
    };

    // Handle close task detail side panel
    const handleCloseTaskDetail = () => {
        setSelectedTaskIdForDetail(null);
        updateURLWithTask(null);
    };
    const handleOpenSubtaskDetail = (projectId, subtaskId) => {
        setSelectedTaskIdForDetail(null);
        updateURLWithTask(null);
        setTimeout(() => {
            setSelectedTaskIdForDetail(subtaskId);
            updateURLWithTask(subtaskId);
        }, 100);
    };
    const handleRefreshMainTaskData = (tasklistId) => {
        fetchTasksForList(tasklistId);
    };

    // Handle update task order
    const handleUpdateTaskOrder = async (taskListId, taskOrders) => {
        if (!jwtToken || !projectId) return;

        try {
            const response = await updateTaskOrder(projectId, taskListId, taskOrders);
            if (response && response.data) {
                // Don't refetch - we've already updated local state optimistically
                // The server now has the correct order
            } else {
                console.error('Failed to update task order');
                // Refetch on error to restore correct state
                await fetchTasksForList(taskListId);
            }
        } catch (err) {
            console.error('Failed to update task order:', err);
            // Refetch on error to restore correct state
            await fetchTasksForList(taskListId);
        }
    };

    // Drag and drop handlers
    const handleDragStart = (e, taskId, taskListId) => {
        setDraggedTaskId(taskId);
        setDraggedTaskListId(taskListId);
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('text/html', taskId);
        if (e.target.tagName === 'TR') {
            e.target.style.opacity = '0.5';
        } else {
            const tr = e.target.closest('tr');
            if (tr) tr.style.opacity = '0.5';
        }
    };

    const handleDragOver = (e, taskId, taskListId) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        // Only allow drag over within the same task list
        if (draggedTaskId && draggedTaskId !== taskId && draggedTaskListId === taskListId) {
            setDraggedOverTaskId(taskId);
        }
    };

    const handleDrop = async (e, targetTaskId) => {
        e.preventDefault();
        e.stopPropagation();
        
        if (!draggedTaskId || !draggedTaskListId || draggedTaskId === targetTaskId) {
            setDraggedTaskId(null);
            setDraggedOverTaskId(null);
            setDraggedTaskListId(null);
            return;
        }

        const currentTasks = tasks[draggedTaskListId] || [];
        const draggedTaskIndex = currentTasks.findIndex(t => t.id === draggedTaskId);
        const targetTaskIndex = currentTasks.findIndex(t => t.id === targetTaskId);

        if (draggedTaskIndex === -1 || targetTaskIndex === -1) {
            setDraggedTaskId(null);
            setDraggedOverTaskId(null);
            setDraggedTaskListId(null);
            return;
        }

        // Reorder tasks in the array
        const newTasks = [...currentTasks];
        const [draggedTask] = newTasks.splice(draggedTaskIndex, 1);
        newTasks.splice(targetTaskIndex, 0, draggedTask);

        // Update local state immediately for better UX
        setTasks(prev => ({
            ...prev,
            [draggedTaskListId]: newTasks
        }));

        // Prepare task orders array for API
        const taskOrders = newTasks.map((task, index) => ({
            id: task.id,
            order: index
        }));

        // Update order on backend
        await handleUpdateTaskOrder(draggedTaskListId, taskOrders);

        setDraggedTaskId(null);
        setDraggedOverTaskId(null);
        setDraggedTaskListId(null);
    };

    const handleDragEnd = (e) => {
        if (e.target.tagName === 'TR') {
            e.target.style.opacity = '1';
        } else {
            const tr = e.target.closest('tr');
            if (tr) tr.style.opacity = '1';
        }
        setDraggedTaskId(null);
        setDraggedOverTaskId(null);
        setDraggedTaskListId(null);
    };


    if (isLoading && !project) {
        return (
            <div className="octopus-single-project">
                <div className="octopus-single-project-loading">
                    <p>Loading project...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="octopus-single-project">
                <div className="octopus-single-project-error">
                    <p>Error: {error}</p>
                    {onBack && <button onClick={onBack}>Go Back</button>}
                </div>
            </div>
        );
    }

    if (!project) {
        return null;
    }

    return (
        <div className="octopus-single-project asana-style">
            {/* Top Header Bar */}
            <div className="asana-project-header">
                <div className="asana-header-left">
                    {onBack && (
                        <button 
                            className="asana-back-btn"
                            onClick={onBack}
                        >
                            <i className="fa-solid fa-arrow-left"></i>
                        </button>
                    )}
                    <div className="asana-project-icon">
                        <i className="fa-solid fa-list-check"></i>
                    </div>
                    <h1 className="asana-project-title">{project.title || project.name}</h1>
                    {project.categories && project.categories.length > 0 && (
                        <div className="asana-project-category">
                            <span className="asana-category-badge">
                                {project.categories[0].title || project.categories[0].name}
                            </span>
                        </div>
                    )}
                    <button
                        className={`asana-star-btn ${isFavorite ? 'active' : ''}`}
                        onClick={handleToggleFavorite}
                        title={isFavorite ? 'Remove from favorites' : 'Add to favorites'}
                    >
                        <i className="fa-solid fa-star"></i>
                    </button>
                    {project.description && (
                        <button
                            className="asana-description-btn"
                            onClick={() => setShowDescription(!showDescription)}
                            title={showDescription ? 'Hide description' : 'Show description'}
                        >
                            <i className={`fa-solid ${showDescription ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                        </button>
                    )}
                    <div className="asana-status-dropdown-wrapper" ref={statusDropdownRef}>
                        <span
                            className="asana-status-btn"
                            onClick={() => setShowStatusDropdown(!showStatusDropdown)}
                        >
                            <i className="fa-solid fa-ellipsis-v"></i>
                        </span>
                        {showStatusDropdown && (
                            <div className="asana-status-dropdown">
                                <button
                                    className="asana-status-dropdown-item asana-status-edit"
                                    onClick={() => {
                                        handleOpenEditModal();
                                        setShowStatusDropdown(false);
                                    }}
                                >
                                    <i className="fa-solid fa-edit"></i>
                                    Edit
                                </button>
                                <button
                                    className="asana-status-dropdown-item asana-status-complete"
                                    onClick={() => {
                                        const newStatus = project.status === 1 ? 0 : 1;
                                        handleChangeStatus(newStatus);
                                        setShowStatusDropdown(false);
                                    }}
                                >
                                    <i className="fa-solid fa-circle-check"></i>
                                    {project.status === 1 ? 'Restore' : 'Complete'}
                                </button>
                                <button
                                    className="asana-status-dropdown-item asana-status-delete"
                                    onClick={() => {
                                        handleDeleteProject();
                                        setShowStatusDropdown(false);
                                    }}
                                >
                                    <i className="fa-solid fa-trash"></i>
                                    Delete
                                </button>
                            </div>
                        )}
                    </div>
                </div>
                <div className="asana-header-right">
                    <div className="asana-action-left">
                        <div className="asana-add-task-wrapper" ref={addTaskDropdownRef}>
                            {taskLists.length === 0 ? (
                            <button
                                className="asana-add-task-btn"
                                onClick={() => {
                                    handleCreateTaskListFromDropdown();
                                }}
                            >
                                <i className="fa-solid fa-plus"></i>
                                Add task
                            </button>
                            ) : (
                                <button
                                    className="asana-add-task-btn"
                                    onClick={handleQuickAddSection}
                                >
                                    <i className="fa-solid fa-plus"></i>
                                    Add Section
                                </button>
                            )}
                        </div>
                    </div>
                    <div className="asana-users-section" ref={addUserPopupRef}>
                        {project.users && project.users.length > 0 && (
                            <div 
                                className="asana-users-avatars-wrapper" 
                                ref={usersPopupRef}
                                onClick={() => setIsManageUsersModalOpen(true)}
                                style={{ cursor: 'pointer' }}
                            >
                                <div className="asana-users-avatars">
                                    {project.users.slice(0, 5).map((user, index) => (
                                        <div 
                                            key={user.ID || user.id || index}
                                            className="asana-user-avatar"
                                            title={user.display_name || user.name || 'User'}
                                            style={{ zIndex: project.users.length - index }}
                                        >
                                            {user.image || user.avatar_url ? (
                                                <img 
                                                    src={user.image || user.avatar_url} 
                                                    alt={user.display_name || user.name || 'User'}
                                                    onError={(e) => {
                                                        e.target.style.display = 'none';
                                                        e.target.nextSibling.style.display = 'flex';
                                                    }}
                                                />
                                            ) : null}
                                            <div 
                                                className="asana-avatar-placeholder"
                                                style={{ display: (user.image || user.avatar_url) ? 'none' : 'flex' }}
                                            >
                                                {(user.display_name || user.name || 'U').charAt(0).toUpperCase()}
                                            </div>
                                        </div>
                                    ))}
                                    {project.users.length > 5 && (
                                        <div 
                                            className="asana-user-avatar-more"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setIsManageUsersModalOpen(true);
                                            }}
                                            title={`${project.users.length - 5} more members`}
                                        >
                                            +{project.users.length - 5}
                                        </div>
                                    )}
                                </div>
                                {openUsersPopupId === 'header' && project.users.length > 5 && (
                                    <div className="asana-users-popup">
                                        {project.users.slice(5).map((user, index) => (
                                            <div key={user.ID || user.id || index} className="asana-popup-user-item">
                                                <div className="asana-popup-avatar">
                                                    {user.image || user.avatar_url ? (
                                                        <img 
                                                            src={user.image || user.avatar_url} 
                                                            alt={user.display_name || user.name || 'User'}
                                                            onError={(e) => {
                                                                e.target.style.display = 'none';
                                                                e.target.nextSibling.style.display = 'flex';
                                                            }}
                                                        />
                                                    ) : null}
                                                    <div 
                                                        className="asana-popup-avatar-placeholder"
                                                        style={{ display: (user.image || user.avatar_url) ? 'none' : 'flex' }}
                                                    >
                                                        {(user.display_name || user.name || 'U').charAt(0).toUpperCase()}
                                                    </div>
                                                </div>
                                                <span className="asana-popup-user-name">
                                                    {user.display_name || user.name || 'User'}
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}
                        <button
                            className="asana-add-user-btn"
                            onClick={() => setIsManageUsersModalOpen(true)}
                            title="Manage users"
                        >
                            <i className="fa-solid fa-plus"></i>
                        </button>

                        
                    </div>
                </div>
            </div>

            {/* Project Description */}
            {project.description && showDescription && (
                <div className="asana-project-description">
                    <p>{project.description}</p>
                </div>
            )}

            {/* Main Content - List View */}
            <div className="asana-list-view">
                    {/* Action Bar */}
                    <div className="asana-action-bar" style={{ display: 'none' }}>
                        <div className="asana-action-left">
                            {/* <div className="asana-add-task-wrapper" ref={addTaskDropdownRef}>
                               {taskLists.length === 0 ? (
                                <button
                                    className="asana-add-task-btn"
                                    onClick={() => {
                                        handleCreateTaskListFromDropdown();
                                    }}
                                >
                                    <i className="fa-solid fa-plus"></i>
                                    Add task
                                </button>
                                ) : (
                                    <button
                                        className="asana-add-task-btn"
                                        onClick={handleQuickAddSection}
                                    >
                                        <i className="fa-solid fa-plus"></i>
                                        Add Section
                                    </button>
                                )}
                            </div> */}
                        </div>
                        <div className="asana-action-right">
                            {/* <button className="asana-action-icon" title="Filter">
                                <i className="fa-solid fa-filter"></i>
                            </button>
                            <button className="asana-action-icon" title="Sort">
                                <i className="fa-solid fa-sort"></i>
                            </button>
                            <button className="asana-action-icon" title="Group">
                                <i className="fa-solid fa-layer-group"></i>
                            </button>
                            <button className="asana-action-icon" title="Options">
                                <i className="fa-solid fa-sliders"></i>
                            </button>
                            <button className="asana-action-icon" title="Search">
                                <i className="fa-solid fa-magnifying-glass"></i>
                            </button> */}
                        </div>
                    </div>

                    {/* Task Sections */}
                    <div className="asana-sections-container">
                        {taskLists.length > 0 && (
                            <>
                                {taskLists.map((taskList) => {
                                    const sectionTasks = tasks[taskList.id] || [];
                                    const isCollapsed = collapsedSections[taskList.id];
                                    return (
                                        <div key={taskList.id} className="asana-section">
                                            <div className="asana-section-header">
                                                <div className="asana-section-header-left">
                                                    <button
                                                        className="asana-section-toggle"
                                                        onClick={() => toggleSection(taskList.id)}
                                                    >
                                                        <i className={`fa-solid fa-chevron-${isCollapsed ? 'right' : 'down'}`}></i>
                                                    </button>
                                                    {editingSectionId === taskList.id ? (
                                                        <input
                                                            type="text"
                                                            className="asana-section-title-input"
                                                            value={editingSectionTitle}
                                                            onChange={(e) => setEditingSectionTitle(e.target.value)}
                                                            onBlur={() => handleSaveSectionTitle(taskList.id)}
                                                            onKeyDown={(e) => {
                                                                if (e.key === 'Enter') {
                                                                    e.preventDefault();
                                                                    handleSaveSectionTitle(taskList.id);
                                                                } else if (e.key === 'Escape') {
                                                                    handleCancelEditSection();
                                                                }
                                                            }}
                                                            placeholder="Section name"
                                                            autoFocus
                                                        />
                                                    ) : (
                                                        <h3 
                                                            className="asana-section-title"
                                                            onClick={() => handleStartEditSection(taskList.id, taskList.title || taskList.name)}
                                                            style={{ cursor: 'pointer' }}
                                                        >
                                                            {taskList.title || taskList.name || 'Untitled section'}
                                                        </h3>
                                                    )}
                                                </div>
                                                <div className="asana-section-actions">
                                                    <div 
                                                        className="asana-section-actions-wrapper"
                                                        ref={el => sectionMenuRefs.current[taskList.id] = el}
                                                    >
                                                        <button
                                                            type="button"
                                                            className="asana-section-action-menu-btn"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                setOpenSectionMenuId(openSectionMenuId === taskList.id ? null : taskList.id);
                                                            }}
                                                            title="Section actions"
                                                        >
                                                            <i className="fa-solid fa-ellipsis-v"></i>
                                                        </button>
                                                        {openSectionMenuId === taskList.id && (
                                                            <div className="asana-section-action-menu">
                                                                <button
                                                                    type="button"
                                                                    className="asana-section-action-menu-item"
                                                                    onClick={() => {
                                                                        handleQuickAddTask(taskList.id);
                                                                        setOpenSectionMenuId(null);
                                                                    }}
                                                                >
                                                                    <i className="fa-solid fa-plus"></i>
                                                                    Add task
                                                                </button>
                                                                <button
                                                                    type="button"
                                                                    className="asana-section-action-menu-item"
                                                                    onClick={() => {
                                                                        handleOpenEditSectionModal(taskList.id);
                                                                        setOpenSectionMenuId(null);
                                                                    }}
                                                                >
                                                                    <i className="fa-solid fa-edit"></i>
                                                                    Edit section
                                                                </button>
                                                                {taskLists.length > 1 && (
                                                                    <button
                                                                        type="button"
                                                                        className="asana-section-action-menu-item asana-section-action-delete"
                                                                        onClick={() => {
                                                                            handleDeleteSection(taskList.id);
                                                                            setOpenSectionMenuId(null);
                                                                        }}
                                                                    >
                                                                        <i className="fa-solid fa-trash"></i>
                                                                        Delete section
                                                                    </button>
                                                                )}
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="asana-section-content">
                                                <small className="asana-section-description">{taskList.description}</small>
                                            </div>
                                            
                                            {!isCollapsed && (
                                                <>
                                                    {/* Always show table with default input row */}
                                                    <div className="asana-tasks-table">
                                                        <table>
                                                            <thead>
                                                                <tr>
                                                                    <th className="asana-col-name">Name</th>
                                                                    <th className="asana-col-assignee">Assignee</th>
                                                                    <th className="asana-col-due">Due-date</th>
                                                                    <th className="asana-col-priority">Priority</th>
                                                                    <th className="asana-col-actions">Actions</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {/* Existing tasks - only show parent tasks (parent_id === 0 or no parent_id) */}
                                                                {sectionTasks.length > 0 && sectionTasks
                                                                    .filter(task => !task.parent_id || task.parent_id === 0)
                                                                    .map((task) => {
                                                                        return (
                                                                            <EditableTaskRow
                                                                                key={task.id}
                                                                                task={task}
                                                                                taskListId={taskList.id}
                                                                                project={project}
                                                                                onUpdate={handleUpdateTask}
                                                                                onDelete={handleDeleteTask}
                                                                                searchUsers={searchUsers}
                                                                                addAssigneeToTask={handleAddAssigneeToTask}
                                                                                onDragStart={handleDragStart}
                                                                                onDragOver={handleDragOver}
                                                                                onDrop={handleDrop}
                                                                                onDragEnd={handleDragEnd}
                                                                                isDragging={draggedTaskId === task.id && draggedTaskListId === taskList.id}
                                                                                isDragOver={draggedOverTaskId === task.id && draggedTaskListId === taskList.id}
                                                                                onOpenDetail={handleOpenTaskDetail}
                                                                                onCreateSubtask={handleCreateSubtask}
                                                                                onUpdateSubtask={handleUpdateSubtask}
                                                                                onDeleteSubtask={handleDeleteSubtask}
                                                                                projectId={projectId}
                                                                            />
                                                                        );
                                                                    })}
                                                                
                                                                {/* Default task input row - always visible */}
                                                                <DefaultTaskInputRow
                                                                    ref={(el) => {
                                                                        if (el) {
                                                                            defaultTaskInputRefs.current[taskList.id] = el;
                                                                        } else {
                                                                            delete defaultTaskInputRefs.current[taskList.id];
                                                                        }
                                                                    }}
                                                                    taskListId={taskList.id}
                                                                    project={project}
                                                                    onSave={handleSaveDraftTask}
                                                                    searchUsers={searchUsers}
                                                                />
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                </>
                                            )}
                                        </div>
                                    );
                                })}
                                
                                {/* Add Section Button */}
                                <button
                                    className="asana-add-section-btn-bottom"
                                    onClick={handleQuickAddSection}
                                >
                                    <i className="fa-solid fa-plus"></i>
                                    Add section
                                </button>
                            </>
                        )}
                    </div>
                </div>

            {/* Create Section Modal */}
            <Modal
                isOpen={isTaskListModalOpen}
                onClose={() => {
                    setIsTaskListModalOpen(false);
                    setTaskListFormData({ title: '', description: '' });
                }}
                title="New Section"
                size="medium"
                footer={
                    <>
                        <button
                            type="button"
                            className="octopus-projects-btn octopus-projects-btn-secondary"
                            onClick={() => {
                                setIsTaskListModalOpen(false);
                                setTaskListFormData({ title: '', description: '' });
                            }}
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            form="task-list-form"
                            className="octopus-projects-btn octopus-projects-btn-primary"
                        >
                            Create
                        </button>
                    </>
                }
            >
                <form id="task-list-form" onSubmit={handleCreateTaskList}>
                    <div className="octopus-form-group">
                        <label htmlFor="task-list-title">Section Title *</label>
                        <input
                            id="task-list-title"
                            type="text"
                            className="octopus-form-input"
                            value={taskListFormData.title}
                            onChange={(e) => setTaskListFormData({ ...taskListFormData, title: e.target.value })}
                            placeholder="Enter section title"
                            required
                        />
                    </div>
                    
                    <div className="octopus-form-group">
                        <label htmlFor="task-list-description">Description</label>
                        <textarea
                            id="task-list-description"
                            className="octopus-form-textarea"
                            value={taskListFormData.description}
                            onChange={(e) => setTaskListFormData({ ...taskListFormData, description: e.target.value })}
                            placeholder="Enter section description (optional)"
                            rows="4"
                        />
                    </div>
                </form>
            </Modal>

            {/* Edit Section Modal */}
            <Modal
                isOpen={isEditSectionModalOpen}
                onClose={() => {
                    setIsEditSectionModalOpen(false);
                    setEditSectionFormData({ title: '', description: '' });
                    setEditingSectionTaskListId(null);
                }}
                title="Edit Section"
                size="medium"
                footer={
                    <>
                        <button
                            type="button"
                            className="octopus-projects-btn octopus-projects-btn-secondary"
                            onClick={() => {
                                setIsEditSectionModalOpen(false);
                                setEditSectionFormData({ title: '', description: '' });
                                setEditingSectionTaskListId(null);
                            }}
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            form="edit-section-form"
                            className="octopus-projects-btn octopus-projects-btn-primary"
                        >
                            Update
                        </button>
                    </>
                }
            >
                <form id="edit-section-form" onSubmit={handleUpdateSection}>
                    <div className="octopus-form-group">
                        <label htmlFor="edit-section-title">Section Title *</label>
                        <input
                            id="edit-section-title"
                            type="text"
                            className="octopus-form-input"
                            value={editSectionFormData.title}
                            onChange={(e) => setEditSectionFormData({ ...editSectionFormData, title: e.target.value })}
                            placeholder="Enter section title"
                            required
                        />
                    </div>
                    
                    <div className="octopus-form-group">
                        <label htmlFor="edit-section-description">Description</label>
                        <textarea
                            id="edit-section-description"
                            className="octopus-form-textarea"
                            value={editSectionFormData.description}
                            onChange={(e) => setEditSectionFormData({ ...editSectionFormData, description: e.target.value })}
                            placeholder="Enter section description (optional)"
                            rows="4"
                        />
                    </div>
                </form>
            </Modal>

            {/* Create Task Modal */}
            <Modal
                isOpen={isTaskModalOpen}
                onClose={() => {
                    setIsTaskModalOpen(false);
                    setTaskFormData({ title: '', description: '', task_list_id: '' });
                    setSelectedTaskListId(null);
                }}
                title="New Task"
                size="medium"
                footer={
                    <>
                        <button
                            type="button"
                            className="octopus-projects-btn octopus-projects-btn-secondary"
                            onClick={() => {
                                setIsTaskModalOpen(false);
                                setTaskFormData({ title: '', description: '', task_list_id: '' });
                                setSelectedTaskListId(null);
                            }}
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            form="task-form"
                            className="octopus-projects-btn octopus-projects-btn-primary"
                        >
                            Create
                        </button>
                    </>
                }
            >
                <form id="task-form" onSubmit={handleCreateTask}>
                    <div className="octopus-form-group">
                        <label htmlFor="task-title">Title *</label>
                        <input
                            id="task-title"
                            type="text"
                            className="octopus-form-input"
                            value={taskFormData.title}
                            onChange={(e) => setTaskFormData({ ...taskFormData, title: e.target.value })}
                            placeholder="Enter task title"
                            required
                        />
                    </div>
                    
                    <div className="octopus-form-group">
                        <label htmlFor="task-description">Description</label>
                        <textarea
                            id="task-description"
                            className="octopus-form-textarea"
                            value={taskFormData.description}
                            onChange={(e) => setTaskFormData({ ...taskFormData, description: e.target.value })}
                            placeholder="Enter task description (optional)"
                            rows="4"
                        />
                    </div>
                </form>
            </Modal>

            {/* Edit Project Modal */}
            <Modal
                isOpen={isEditProjectModalOpen}
                onClose={() => {
                    setIsEditProjectModalOpen(false);
                    setEditProjectFormData({ title: '', description: '', category: '', assignees: [] });
                    setUserSearchQuery('');
                    setShowUserDropdown(false);
                }}
                title="Edit Project"
                size="medium"
                footer={
                    <>
                        <button
                            type="button"
                            className="octopus-projects-btn octopus-projects-btn-secondary"
                            onClick={() => {
                                setIsEditProjectModalOpen(false);
                                setEditProjectFormData({ title: '', description: '', category: '', assignees: [] });
                                setUserSearchQuery('');
                                setShowUserDropdown(false);
                            }}
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            form="edit-project-form"
                            className="octopus-projects-btn octopus-projects-btn-primary"
                        >
                            Update
                        </button>
                    </>
                }
            >
                <form id="edit-project-form" onSubmit={handleUpdateProject}>
                    <div className="octopus-form-group">
                        <label htmlFor="edit-project-title">Title *</label>
                        <input
                            id="edit-project-title"
                            type="text"
                            className="octopus-form-input"
                            value={editProjectFormData.title}
                            onChange={(e) => setEditProjectFormData({ ...editProjectFormData, title: e.target.value })}
                            placeholder="Enter project title"
                            required
                        />
                    </div>
                    
                    <div className="octopus-form-group">
                        <label htmlFor="edit-project-category">Category</label>
                        <select
                            id="edit-project-category"
                            className="octopus-form-input"
                            value={editProjectFormData.category}
                            onChange={(e) => setEditProjectFormData({ ...editProjectFormData, category: e.target.value })}
                        >
                            <option value="">- Select Category -</option>
                            {categories.map((category) => (
                                <option 
                                    key={category.id || category.category_id} 
                                    value={category.id || category.category_id}
                                >
                                    {category.name || category.title}
                                </option>
                            ))}
                        </select>
                    </div>
                    
                    <div className="octopus-form-group">
                        <label htmlFor="edit-project-description">Description</label>
                        <textarea
                            id="edit-project-description"
                            className="octopus-form-textarea"
                            value={editProjectFormData.description}
                            onChange={(e) => setEditProjectFormData({ ...editProjectFormData, description: e.target.value })}
                            placeholder="Enter project description (optional)"
                            rows="4"
                        />
                    </div>
                </form>
            </Modal>

            {/* Manage Users Modal */}
            <Modal
                isOpen={isManageUsersModalOpen}
                onClose={() => {
                    setIsManageUsersModalOpen(false);
                    setManageUsersSearchQuery('');
                    setManageUsersSearchResults([]);
                }}
                title="Manage Project Members"
                size="medium"
            >
                <div className="octopus-manage-users-modal">
                    {/* Assigned Users List */}
                    <div className="octopus-assigned-users-section">
                        <h4>Assigned Members ({project?.users?.length || 0})</h4>
                        {project?.users && project.users.length > 0 ? (
                            <div className="octopus-assigned-users-list">
                                {project.users.map((user) => (
                                    <div key={user.ID || user.id} className="octopus-assigned-user-item">
                                        <div className="octopus-user-info">
                                            <div className="octopus-user-avatar-medium">
                                                {user.image || user.avatar_url ? (
                                                    <img 
                                                        src={user.image || user.avatar_url} 
                                                        alt={user.display_name || user.name || 'User'}
                                                        onError={(e) => {
                                                            e.target.style.display = 'none';
                                                            e.target.nextSibling.style.display = 'flex';
                                                        }}
                                                    />
                                                ) : null}
                                                <div 
                                                    className="octopus-user-avatar-placeholder"
                                                    style={{ display: (user.image || user.avatar_url) ? 'none' : 'flex' }}
                                                >
                                                    {(user.display_name || user.name || 'U').charAt(0).toUpperCase()}
                                                </div>
                                            </div>
                                            <div className="octopus-user-details">
                                                <span className="octopus-user-name">
                                                    {user.display_name || user.name || 'User'}
                                                </span>
                                                {user.email && (
                                                    <span className="octopus-user-email">{user.email}</span>
                                                )}
                                            </div>
                                        </div>
                                        {parseInt(user.ID) === parseInt(project.created_by) ? (
                                            <span className="octopus-user-role">Owner</span>
                                        ) : parseInt(user.ID) === parseInt(currentUserId) ? (
                                            <span className="octopus-user-role">You</span>
                                        ) : null}
                                        {parseInt(user.ID) !== parseInt(project.created_by) && parseInt(user.ID) !== parseInt(currentUserId) && (
                                            <button
                                                type="button"
                                                className="octopus-remove-user-btn"
                                                onClick={() => handleRemoveUserFromProject(user.ID || user.id)}
                                                title="Remove user"
                                            >
                                                <i className="fa-solid fa-times"></i>
                                            </button>
                                        )}
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="octopus-no-users">
                                <p>No members assigned to this project</p>
                            </div>
                        )}
                    </div>

                    {/* Add Users Section */}
                    <div className="octopus-add-users-section">
                        <h4>Add Members</h4>
                        <div className="octopus-user-search-wrapper" ref={manageUsersSearchRef}>
                            <input
                                type="text"
                                className="octopus-form-input"
                                placeholder="Search users to add..."
                                value={userSearchQuery}
                                onChange={(e) => setUserSearchQuery(e.target.value)}
                                onFocus={() => {
                                    if (searchResults.length > 0) {
                                        setShowUserDropdown(true);
                                    }
                                }}
                            />
                            {isSearching && (
                                <div className="octopus-search-loading">
                                    <i className="fa-solid fa-spinner fa-spin"></i>
                                </div>
                            )}
                        </div>

                        {userSearchQuery.trim() && (
                            <div className="octopus-search-results">
                                {isSearching ? (
                                    <div className="octopus-search-loading">
                                        <i className="fa-solid fa-spinner fa-spin"></i> Searching...
                                    </div>
                                ) : searchResults.length > 0 ? (
                                    searchResults.map((user) => (
                                        <div
                                            key={user.ID || user.id}
                                            className="octopus-search-result-item"
                                            onClick={() => handleAddUserFromModal(user)}
                                        >
                                            <div className="octopus-user-avatar-medium">
                                                {user.image || user.avatar_url ? (
                                                    <img 
                                                        src={user.image || user.avatar_url} 
                                                        alt={user.display_name || user.name || 'User'}
                                                        onError={(e) => {
                                                            e.target.style.display = 'none';
                                                            e.target.nextSibling.style.display = 'flex';
                                                        }}
                                                    />
                                                ) : null}
                                                <div 
                                                    className="octopus-user-avatar-placeholder"
                                                    style={{ display: (user.image || user.avatar_url) ? 'none' : 'flex' }}
                                                >
                                                    {(user.display_name || user.name || 'U').charAt(0).toUpperCase()}
                                                </div>
                                            </div>
                                            <div className="octopus-user-details">
                                                <span className="octopus-user-name">
                                                    {user.display_name || user.name || 'User'}
                                                </span>
                                                {user.email && (
                                                    <span className="octopus-user-email">{user.email}</span>
                                                )}
                                            </div>
                                            <button
                                                type="button"
                                                className="octopus-add-user-btn"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleAddUserFromModal(user);
                                                }}
                                                title="Add user"
                                            >
                                                <i className="fa-solid fa-plus"></i>
                                            </button>
                                        </div>
                                    ))
                                ) : (
                                    <div className="octopus-search-empty">
                                        No users found
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </Modal>

            {/* Task Detail Side Panel */}
            {selectedTaskIdForDetail && (
                <TaskDetailSidePanel
                    taskId={selectedTaskIdForDetail}
                    currentUserId={currentUserId}
                    projectId={projectId}
                    onClose={handleCloseTaskDetail}
                    onOpenSubtaskDetail={handleOpenSubtaskDetail}
                    onRefreshMainTaskData={handleRefreshMainTaskData}
                    OnNotFound={handleOnNotFound}
                />
            )}
        </div>
    );
};

export default SingleProjectView;

